/* ═════════════════════════════════════════════════════════
   Design photograph viewer

   A card used to swap to one alternate view on hover and stop there, so the third and
   fourth photographs (the detail shot, the villa shot) had no way out at all. Each design
   gets a lightbox instead: arrow through the views, click to magnify to 2.5x, drag to
   read the detail.

   Load-bearing, do not simplify away:
   · The shot list comes from the page, never hardcoded here - the factory adds a design
     in Shopify admin and the viewer follows without anybody touching this file.
   · Touch: one finger pans, two fingers pinch to zoom.
   · Keyboard: left/right arrow to page, + / − to zoom, Esc to close, Tab stays trapped
     inside the lightbox.
   · Closing returns focus to the card that opened it - without that, a screen-reader user
     is dropped back at the top of the page.
   · Every node here is built by JS, so with JS off there are no dead buttons, just the
     card's own link.
   ═════════════════════════════════════════════════════════ */
(function () {
  "use strict";
  var cards = [].slice.call(document.querySelectorAll(".pr-m"));
  if (!cards.length) return;                       // not a page with design cards

  var reduced = matchMedia("(prefers-reduced-motion: reduce)").matches;
  var MODEL = {};            // code -> [{src,w,h}]
  var box, img, cap, dots, prevB, nextB, zoomB, closeB;
  var cur = { code: null, i: 0, z: 1, x: 0, y: 0 }, opener = null;

  /* ── every view of every design ─────────────────────────────────── */
  /* ⚠ MODEL is built from the DOM, not fetched.

     The static site read assets/products/products.json. The theme has no such file: the
     designs are real Shopify products, so the request 404'd, MODEL stayed empty, open()
     returned false and every card silently fell through to its href - the fetch's empty
     .catch meant there was no symptom to notice. sections/pr-idx.liquid now prints each
     design's shot list onto its own card as data-shots. */
  function buildModel() {
    [].forEach.call(cards, function (card) {
      var codeEl = card.querySelector(".code");
      if (!codeEl) return;
      var code = codeEl.textContent.trim();
      var raw = card.getAttribute("data-shots");
      if (!code || !raw) return;
      var urls;
      try { urls = JSON.parse(raw); } catch (e) { return; }
      if (!urls || !urls.length) return;
      MODEL[code] = {
        shots: urls.map(function (u) { return { src: u, kind: "" }; }),
        series: card.getAttribute("data-series") || "",
        cat: card.getAttribute("data-cat") || ""
      };
    });
  }
  buildModel();

  /* ── the lightbox itself ─────────────────────────────────────────── */
  function build() {
    box = document.createElement("div");
    box.className = "zm";
    box.setAttribute("role", "dialog");
    box.setAttribute("aria-modal", "true");
    box.setAttribute("aria-label", "Product images");
    box.innerHTML =
      '<div class="zm-bg" data-close></div>' +
      '<div class="zm-in">' +
        '<div class="zm-stage" data-stage><img class="zm-img" alt=""></div>' +
        '<div class="zm-bar">' +
          '<button class="zm-b" data-prev aria-label="Previous view">&#8592;</button>' +
          '<span class="zm-cap"></span>' +
          '<span class="zm-dots"></span>' +
          '<button class="zm-b" data-zoom aria-label="Zoom in">&#43;</button>' +
          '<button class="zm-b" data-next aria-label="Next view">&#8594;</button>' +
        '</div>' +
        '<button class="zm-x" data-close aria-label="Close">&#10005;</button>' +
      '</div>';
    document.body.appendChild(box);
    img = box.querySelector(".zm-img");
    cap = box.querySelector(".zm-cap");
    dots = box.querySelector(".zm-dots");
    prevB = box.querySelector("[data-prev]");
    nextB = box.querySelector("[data-next]");
    zoomB = box.querySelector("[data-zoom]");

    [].slice.call(box.querySelectorAll("[data-close]")).forEach(function (b) {
      b.addEventListener("click", close);
    });
    prevB.addEventListener("click", function () { step(-1); });
    nextB.addEventListener("click", function () { step(1); });
    zoomB.addEventListener("click", function () { setZoom(cur.z >= 2.5 ? 1 : cur.z + .75); });
    box.querySelector("[data-stage]").addEventListener("click", function (e) {
      if (e.target === img && cur.z === 1) setZoom(2.5, e);
    });
    wireDrag(box.querySelector("[data-stage]"));
  }

  /* ── zoom and pan ───────────────────────────────────────── */
  function clampPan() {
    var st = box.querySelector("[data-stage]").getBoundingClientRect();
    var lim = function (v, span) { return Math.max(-span, Math.min(span, v)); };
    var sx = st.width * (cur.z - 1) / 2, sy = st.height * (cur.z - 1) / 2;
    cur.x = lim(cur.x, sx); cur.y = lim(cur.y, sy);
  }
  function paintZoom() {
    clampPan();
    img.style.transform = "translate(" + cur.x + "px," + cur.y + "px) scale(" + cur.z + ")";
    box.classList.toggle("is-zoom", cur.z > 1);
    zoomB.setAttribute("aria-label", cur.z > 1 ? "Reset zoom" : "Zoom in");
    zoomB.innerHTML = cur.z > 1 ? "&minus;" : "&#43;";
  }
  function setZoom(z, e) {
    var was = cur.z;
    cur.z = Math.max(1, Math.min(3.5, z));
    if (cur.z === 1) { cur.x = cur.y = 0; }
    else if (e && was === 1) {
      // magnify about the click, so what was under the pointer stays under it
      var st = box.querySelector("[data-stage]").getBoundingClientRect();
      cur.x = (st.left + st.width / 2 - e.clientX) * (cur.z - 1);
      cur.y = (st.top + st.height / 2 - e.clientY) * (cur.z - 1);
    }
    paintZoom();
  }

  function wireDrag(stage) {
    var pts = {}, last = null, pinch0 = 0, z0 = 1;
    stage.addEventListener("pointerdown", function (e) {
      pts[e.pointerId] = e;
      stage.setPointerCapture(e.pointerId);
      if (Object.keys(pts).length === 2) {
        var p = Object.keys(pts).map(function (k) { return pts[k]; });
        pinch0 = Math.hypot(p[0].clientX - p[1].clientX, p[0].clientY - p[1].clientY);
        z0 = cur.z;
      } else { last = e; }
    });
    stage.addEventListener("pointermove", function (e) {
      if (!pts[e.pointerId]) return;
      pts[e.pointerId] = e;
      var ids = Object.keys(pts);
      if (ids.length === 2 && pinch0) {         // two fingers, pinch
        var p = ids.map(function (k) { return pts[k]; });
        var d = Math.hypot(p[0].clientX - p[1].clientX, p[0].clientY - p[1].clientY);
        cur.z = Math.max(1, Math.min(3.5, z0 * d / pinch0));
        if (cur.z === 1) cur.x = cur.y = 0;
        paintZoom();
      } else if (ids.length === 1 && last && cur.z > 1) {   // one finger, pan
        cur.x += e.clientX - last.clientX;
        cur.y += e.clientY - last.clientY;
        last = e;
        paintZoom();
      }
    });
    ["pointerup", "pointercancel"].forEach(function (t) {
      stage.addEventListener(t, function (e) {
        delete pts[e.pointerId];
        if (Object.keys(pts).length < 2) pinch0 = 0;
        last = null;
      });
    });
    stage.addEventListener("wheel", function (e) {
      if (!box.classList.contains("is-on")) return;
      e.preventDefault();
      setZoom(cur.z + (e.deltaY < 0 ? .3 : -.3), e);
    }, { passive: false });
  }

  /* ── next view, next design ──────────────────────────────────────── */
  function show(i) {
    var m = MODEL[cur.code];
    if (!m) return;
    cur.i = (i + m.shots.length) % m.shots.length;
    var sh = m.shots[cur.i];
    img.src = sh.src;
    img.alt = m.series + " " + m.cat + ", model " + cur.code + " — view " + (cur.i + 1);
    cap.textContent = cur.code + " · " + m.series;
    dots.innerHTML = m.shots.map(function (_, k) {
      return '<i class="' + (k === cur.i ? "on" : "") + '"></i>';
    }).join("");
    var one = m.shots.length < 2;
    prevB.hidden = nextB.hidden = one;
    cur.z = 1; cur.x = cur.y = 0; paintZoom();
  }
  function step(d) { show(cur.i + d); }

  /* ── open and close ──────────────────────────────────────────── */
  function open(code, trigger) {
    if (!MODEL[code]) return false;
    if (!box) build();
    opener = trigger;
    cur.code = code;
    show(0);
    box.classList.add("is-on");
    document.documentElement.style.overflow = "hidden";
    setTimeout(function () { box.querySelector(".zm-x").focus(); }, 30);
    return true;
  }
  function close() {
    if (!box) return;
    box.classList.remove("is-on");
    document.documentElement.style.overflow = "";
    if (opener) { try { opener.focus(); } catch (e) {} opener = null; }
  }

  addEventListener("keydown", function (e) {
    if (!box || !box.classList.contains("is-on")) return;
    if (e.key === "Escape") { close(); return; }
    if (e.key === "ArrowLeft") { step(-1); e.preventDefault(); }
    if (e.key === "ArrowRight") { step(1); e.preventDefault(); }
    if (e.key === "+" || e.key === "=") { setZoom(cur.z + .75); e.preventDefault(); }
    if (e.key === "-" || e.key === "_") { setZoom(cur.z - .75); e.preventDefault(); }
    if (e.key === "Tab") {                       // focus stays trapped inside the lightbox
      var f = [].slice.call(box.querySelectorAll("button:not([hidden])"));
      if (!f.length) return;
      var i = f.indexOf(document.activeElement);
      var n = e.shiftKey ? (i <= 0 ? f.length - 1 : i - 1) : (i === f.length - 1 ? 0 : i + 1);
      f[n].focus(); e.preventDefault();
    }
  });

  /* ── taking over the card's click ─────────────────────────────────── */
  // The card is an <a> to the quote page. The lightbox takes the click but keeps
  // the href as the no-JS fallback, and lets Cmd/Ctrl-click open a new tab.
  cards.forEach(function (card) {
    card.addEventListener("click", function (e) {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button) return;
      var code = (card.querySelector(".code") || {}).textContent;
      if (code && open(code.trim(), card)) e.preventDefault();
    });
  });
})();
